/**
 * Filtrous Meta Filters — shared multi-select filter system
 * Used on both collection pages and search results.
 * Builds clickable pill buttons from product card data-* attributes.
 * Supports multi-select (pick multiple values per filter category).
 */
(function() {
  var LABEL_MAP = {
    'membrane': 'Membrane Type', 'pore_size': 'Pore Size', 'diameter': 'Diameter',
    'layers': 'Layers', 'sterility': 'Sterility', 'quantity': 'Quantity',
    'closure_type': 'Closure Type', 'thread_size': 'Thread Size',
    'volume-metric': 'Volume', 'vial_color': 'Vial Color', 'cap_color': 'Cap Color',
    'septum_material': 'Septum Material', 'cap_assembly': 'Cap Assembly',
    'well_count': 'Well Count', 'profile': 'Profile', 'skirt_type': 'Skirt Type',
    'material': 'Material', 'platform': 'Platform', 'cap_style': 'Cap Style',
    'kit_type': 'Kit Type', 'instrument_compatibility': 'Instrument',
    'rxn_count': 'Reaction Count', 'format': 'Format', 'treatment': 'Treatment',
    'dimensions': 'Dimensions', 'filter': 'Filter', 'retention': 'Retention',
    'for_use_with_equipment_': 'For Use With', 'channels': 'Channels',
    'range': 'Range', 'brand': 'Brand', 'tip_type': 'Tip Type', 'plunger': 'Plunger',
    'ph': 'pH', 'concentration': 'Concentration', 'grade': 'Grade', 'feature': 'Feature',
    'extraction_method': 'Extraction Method', 'prep_count': 'Prep Count',
    'scale': 'Scale', 'shape': 'Shape', 'type': 'Type', 'size': 'Size',
    'thickness': 'Thickness', 'additive': 'Additive', 'gauge': 'Gauge',
    'compliance': 'Compliance', 'matrix_type': 'Matrix Type', 'magnetic': 'Magnetic',
    'fluorescent_protein': 'Fluorescent Protein', 'sorbent': 'Sorbent',
    'method': 'Method', 'anti_static': 'Anti-Static', 'disposable': 'Disposable',
    'color': 'Color', 'septa_type': 'Septum Type', 'cap_type': 'Cap Type',
    'cap_thread': 'Cap Thread', 'patch': 'Patch', 'pack_qty': 'Pack Size', 'name': 'Name'
  };

  function keyToAttr(key) {
    if (key === 'volume-metric') return 'data-volume';
    return 'data-' + key.replace(/_+$/, '').replace(/_/g, '-');
  }

  function formatLabel(key) {
    return LABEL_MAP[key] || key.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  }

  /**
   * Extract available filter values from product cards in a container.
   * @param {Element} container - element containing .fn-card elements
   * @param {string[]} filterKeys - metafield keys to scan
   * @returns {Object} { dataAttr: { label, values: [{value, count}] } }
   */
  function extractFilters(container, filterKeys) {
    var productCards = container.querySelectorAll('.fn-card');
    var filters = {};
    filterKeys.forEach(function(key) {
      var attr = keyToAttr(key);
      var valueCounts = {};
      productCards.forEach(function(card) {
        var val = card.getAttribute(attr);
        if (val && val !== '[]') {
          valueCounts[val] = (valueCounts[val] || 0) + 1;
        }
      });
      var uniqueValues = Object.keys(valueCounts);
      if (uniqueValues.length > 1) {
        filters[attr] = {
          label: formatLabel(key),
          values: uniqueValues.sort().map(function(v) {
            return { value: v, count: valueCounts[v] };
          })
        };
      }
    });
    return filters;
  }

  /**
   * Apply active filters to product cards — multi-select (OR within category, AND across categories).
   * @param {Element} container - element containing .fn-card elements
   * @param {Object} activeFilters - { dataAttr: [value1, value2, ...] }
   * @returns {number} count of visible products
   */
  function applyFilters(container, activeFilters) {
    var productCards = container.querySelectorAll('.fn-card');
    var attrs = Object.keys(activeFilters);
    var visibleCount = 0;

    if (attrs.length === 0) {
      productCards.forEach(function(c) {
        c.style.display = '';
        c.removeAttribute('data-hidden-by-filter');
      });
      return productCards.length;
    }

    productCards.forEach(function(card) {
      var show = true;
      attrs.forEach(function(attr) {
        var cardVal = card.getAttribute(attr) || '';
        var selectedVals = activeFilters[attr];
        if (selectedVals.length > 0 && selectedVals.indexOf(cardVal) === -1) {
          show = false;
        }
      });
      card.style.display = show ? '' : 'none';
      if (show) {
        card.removeAttribute('data-hidden-by-filter');
        visibleCount++;
      } else {
        card.setAttribute('data-hidden-by-filter', '1');
      }
    });
    return visibleCount;
  }

  /**
   * Build the filter bar UI with collapsible pill groups.
   * @param {Element} container - element containing .fn-card products
   * @param {Element} insertBefore - element to insert the filter bar before
   * @param {Object} activeFilters - shared state object { attr: [values] }
   * @param {Function} onFilterChange - callback after filters change
   * @returns {Element|null} the created filter bar element
   */
  function buildFilterBar(container, insertBefore, activeFilters, onFilterChange) {
    var filterKeysStr = container.getAttribute('data-filter-keys');
    if (!filterKeysStr) return null;
    var filterKeys = filterKeysStr.split(',').map(function(k) { return k.trim(); });

    var filters = extractFilters(container, filterKeys);
    var filterAttrs = Object.keys(filters);
    if (filterAttrs.length === 0) return null;

    var bar = document.createElement('div');
    bar.className = 'fmf';

    /* ── Active pills row (shows selected values) ── */
    var pillsRow = document.createElement('div');
    pillsRow.className = 'fmf__active-pills';
    bar.appendChild(pillsRow);

    /* ── Filter groups ── */
    var groupsRow = document.createElement('div');
    groupsRow.className = 'fmf__groups';

    filterAttrs.forEach(function(attr) {
      var f = filters[attr];
      var group = document.createElement('div');
      group.className = 'fmf__group';

      /* Toggle button */
      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'fmf__toggle';
      toggle.innerHTML = '<span class="fmf__toggle-label">' + f.label + '</span>' +
        '<svg class="fmf__toggle-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M1 1l4 4 4-4"/></svg>';

      /* Dropdown panel */
      var panel = document.createElement('div');
      panel.className = 'fmf__panel';

      f.values.forEach(function(item) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'fmf__pill';
        pill.setAttribute('data-attr', attr);
        pill.setAttribute('data-value', item.value);
        pill.innerHTML = '<span class="fmf__pill-text">' + item.value + '</span>' +
          '<span class="fmf__pill-count">' + item.count + '</span>';

        if (activeFilters[attr] && activeFilters[attr].indexOf(item.value) !== -1) {
          pill.classList.add('is-active');
        }

        pill.addEventListener('click', function(e) {
          e.stopPropagation();
          if (!activeFilters[attr]) activeFilters[attr] = [];
          var idx = activeFilters[attr].indexOf(item.value);
          if (idx === -1) {
            activeFilters[attr].push(item.value);
            pill.classList.add('is-active');
          } else {
            activeFilters[attr].splice(idx, 1);
            pill.classList.remove('is-active');
            if (activeFilters[attr].length === 0) delete activeFilters[attr];
          }
          var count = applyFilters(container, activeFilters);
          updateActivePills(pillsRow, activeFilters, container, onFilterChange);
          updateToggleStates(groupsRow, activeFilters);
          if (onFilterChange) onFilterChange(count);
        });

        panel.appendChild(pill);
      });

      toggle.addEventListener('click', function() {
        var isOpen = group.classList.contains('is-open');
        /* Close all other groups */
        groupsRow.querySelectorAll('.fmf__group.is-open').forEach(function(g) {
          g.classList.remove('is-open');
        });
        if (!isOpen) group.classList.add('is-open');
      });

      group.appendChild(toggle);
      group.appendChild(panel);
      groupsRow.appendChild(group);
    });

    bar.appendChild(groupsRow);

    /* Close panels when clicking outside */
    document.addEventListener('click', function(e) {
      if (!bar.contains(e.target)) {
        groupsRow.querySelectorAll('.fmf__group.is-open').forEach(function(g) {
          g.classList.remove('is-open');
        });
      }
    });

    insertBefore.parentNode.insertBefore(bar, insertBefore);
    updateActivePills(pillsRow, activeFilters, container, onFilterChange);
    updateToggleStates(groupsRow, activeFilters);
    return bar;
  }

  function updateToggleStates(groupsRow, activeFilters) {
    groupsRow.querySelectorAll('.fmf__toggle').forEach(function(toggle) {
      var group = toggle.parentNode;
      var pills = group.querySelectorAll('.fmf__pill.is-active');
      toggle.classList.toggle('has-active', pills.length > 0);
      /* Update count badge on toggle */
      var badge = toggle.querySelector('.fmf__toggle-badge');
      if (pills.length > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'fmf__toggle-badge';
          toggle.querySelector('.fmf__toggle-label').after(badge);
        }
        badge.textContent = pills.length;
      } else if (badge) {
        badge.remove();
      }
    });
  }

  function updateActivePills(pillsRow, activeFilters, container, onFilterChange) {
    pillsRow.innerHTML = '';
    var attrs = Object.keys(activeFilters);
    if (attrs.length === 0) {
      pillsRow.style.display = 'none';
      return;
    }
    pillsRow.style.display = '';

    attrs.forEach(function(attr) {
      activeFilters[attr].forEach(function(val) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'fmf__active-pill';
        pill.innerHTML = '<span>' + val + '</span>' +
          '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M2 2l6 6M8 2l-6 6"/></svg>';
        pill.addEventListener('click', function() {
          var idx = activeFilters[attr].indexOf(val);
          if (idx !== -1) activeFilters[attr].splice(idx, 1);
          if (activeFilters[attr].length === 0) delete activeFilters[attr];
          var count = applyFilters(container, activeFilters);
          updateActivePills(pillsRow, activeFilters, container, onFilterChange);
          /* Update pill states in the panel */
          var panelPill = container.parentNode.querySelector('.fmf__pill[data-attr="' + attr + '"][data-value="' + val + '"]');
          if (panelPill) panelPill.classList.remove('is-active');
          var groupsRow = container.parentNode.querySelector('.fmf__groups');
          if (groupsRow) updateToggleStates(groupsRow, activeFilters);
          if (onFilterChange) onFilterChange(count);
        });
        pillsRow.appendChild(pill);
      });
    });

    /* Clear all button */
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'fmf__clear-all';
    clearBtn.textContent = 'Clear all';
    clearBtn.addEventListener('click', function() {
      Object.keys(activeFilters).forEach(function(k) { delete activeFilters[k]; });
      applyFilters(container, activeFilters);
      updateActivePills(pillsRow, activeFilters, container, onFilterChange);
      container.parentNode.querySelectorAll('.fmf__pill.is-active').forEach(function(p) {
        p.classList.remove('is-active');
      });
      var groupsRow = container.parentNode.querySelector('.fmf__groups');
      if (groupsRow) updateToggleStates(groupsRow, activeFilters);
      if (onFilterChange) onFilterChange(container.querySelectorAll('.fn-card').length);
    });
    pillsRow.appendChild(clearBtn);
  }

  /* Export to window for use by both search and collection pages */
  window.FiltrousMetaFilters = {
    buildFilterBar: buildFilterBar,
    applyFilters: applyFilters,
    extractFilters: extractFilters
  };
})();
